import ShippingId from '@/components/Shippings/ListItemId'
import ShippingExternalId from '@/components/Shippings/ListItemExternalId'
import ShippingInstructions from '@/components/Shippings/ListItemInstructions'
import ShippingsListItemStatus from '@/components/Shippings/ListItemStatus'
import ShippingCheckpoint from '@/components/Shippings/Checkpoint'
import EditFieldItems from '@/components/Shippings/EditFieldItems'
import EditFieldCourier from '@/components/Shippings/EditFieldCourier'
import CompanyAutocomplete from '@/components/Autocompletes/CompanyAutocomplete'
import { sanitizeModel } from '@/common/utils/modelDecorator'

export default {
  computed: {
    shippingsModel() {
      return sanitizeModel({
        title: 'Shippings',
        endpoint: this.$appConfig.api.baseUrl + 'shippings',
        listExtrasComponent: () => import('@/components/Shippings/ListExtras'),
        allowSelect: true,
        countrize: 'from_country_iso_code',
        fields: [
          {
            key: 'id',
            sortable: false,
            editable: false,
            label: this.$gettext('ID'),
            component: (value, key, item) => {
              return {
                is: ShippingId,
                shipping: item
              }
            }
          },
          {
            key: 'company_id',
            sortable: false,
            label: this.$gettext('Customer'),
            formatter: (value, key, item) => {
              if (item.company) {
                let html = `<strong>${item.company.external_id || item.company.name || item.company.id}</strong>`
                html += item.company ? `<br><span class="badge" style="background-color: ${this.stringToColour(' ' + item.company.id + item.company.id + item.company.id + item.company.id + item.company.id)};${this.textContrastColor(this.stringToColour(' ' + item.company.id + item.company.id + item.company.id + item.company.id + item.company.id))}">${item.company.name}</span>` : ''
                return html
              }
            }
          },
          {
            key: 'from_contact',
            sortable: true,
            label: this.$gettext('Origin'),
            class: 'col-contact',
            component: (value, key, item) => {
              return {
                is: ShippingCheckpoint,
                shipping: item,
                checkpoint: 'from',
                withEdit: !this.ecosystemConfig.disableEditOrigins,
                withIcons: true,
                withTooltip: true,
                withExtraCheckpoints: true
              }
            }
          },
          {
            key: 'from_address',
            sortable: false,
            label: this.$gettext('Origin Address')
          },
          // {
          //   key: 'from_email',
          //   sortable: true
          // },
          {
            key: 'to_contact',
            sortable: false,
            label: this.$gettext('Destination'),
            class: 'col-contact',
            component: (value, key, item) => {
              return {
                is: ShippingCheckpoint,
                shipping: item,
                checkpoint: 'to',
                withEdit: !this.ecosystemConfig.disableEditDestinations,
                withIcons: true,
                withTooltip: true,
                withExtraCheckpoints: true
              }
            }
          },
          {
            key: 'to_address',
            sortable: false,
            label: this.$gettext('Destination Address')
          },
          {
            key: 'instructions',
            sortable: false,
            label: this.$gettext('Instruct.'),
            component: (value, key, item) => {
              return {
                is: ShippingInstructions,
                shipping: item
              }
            }
          },
          {
            key: 'scheduled_date',
            sortable: true,
            label: this.$gettext('Sched. At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'status',
            sortable: true,
            label: this.$gettext('Status'),
            component: (value, key, item) => {
              return {
                is: ShippingsListItemStatus,
                shipping: item,
                withEta: true
              }
            },
            class: 'text-center'
          },
          {
            key: 'courier_company_id',
            sortable: false,
            label: this.$gettext('Carrier'),
            formatter: (value, key, item) => {
              if (item.courier_company) {
                let html = `<strong>${item.courier_company.external_id || item.courier_company.name || item.courier_company.id}</strong>`
                html += item.courier_company ? `<br><span class="badge" style="background-color: ${this.stringToColour(' ' + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id)};${this.textContrastColor(this.stringToColour(' ' + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id))}">${item.courier_company.name}</span>` : ''
                return html
              }
            }
          },
          {
            key: 'courier',
            sortable: false,
            label: this.$gettext('Courier'),
            component: (value, key, item) => {
              return {
                is: 'user-card',
                user: item.courier || {},
                profilePicture: false,
                withTitles: false
                // " :vehicle-types-prop="vehicleTypes"
              }
            }
          },
          {
            key: 'courier_commission',
            label: this.$gettext('Commission'),
            type: 'number',
            hidden: true,
            class: 'text-center',
            formatter: (value, key, item) => {
              return this.$options.filters.currency(value, window.country)
            }
          },
          {
            key: 'size_id',
            sortable: false,
            label: this.$gettext('Size'),
            formatter: (value, key, item) => {
              return this.shippingSizes.some(x => x.id === value) ? this.shippingSizes.find(x => x.id === value).name : ''
            }
          },
          {
            key: 'shipping_type_id',
            sortable: false,
            label: this.$gettext('Type'),
            formatter: (value, key, item) => {
              let html = this.shippingTypesShared.some(x => x.value === value) ? this.shippingTypesShared.find(x => x.value === value).text : '-'
              if (item.tags && item.tags.length > 0) {
                for (const tag of item.tags) {
                  html += '<span class="badge badge-pill">' + tag + '</span>&nbsp;'
                }
              }
              return html
            }
          },
          {
            key: 'estimated_delivery_date',
            sortable: true,
            editable: false,
            label: this.$gettext('Est. Delivery Date'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'created_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Created At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'updated_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Updated At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          }
        ],
        extraActions: [
          {
            label: '',
            type: () => import('@/components/Shippings/ListItemActions'), // external bunch of actions
            position: 'start',
            props: (item) => {
              return {
                shipping: item
              }
            }
          },
          {
            type: 'b-dropdown-divider',
            position: 'start'
          }
        ],
        filters: [
          'id',
          'courier_id',
          'from_place_id',
          'from_contact',
          'address_from.formatted_address',
          'from_email',
          'from_phone',
          'to_contact',
          'address_to.formatted_address',
          'to_email',
          'to_phone',
          'status',
          'scheduled_date',
          'created_at',
          'user_id',
          'company_id',
          'shipping_type_id',
          'tour_id'
        ],
        includes: [
          'courier.courier_available.tracking',
          'courier.roles',
          'courier_company',
          'courier.company',
          'company',
          'user',
          'status_history',
          'pending_hooks',
          'tags',
          'threads',
          'previousShipping',
          'followingShippings',
          'items',
          'items.item_tracking'
        ],
        exclude: [
          'from_address',
          'to_address',
          (!this.ecosystemModules.carriers ? 'courier_company_id' : '')
        ]
      })
    },
    shippingsAsCourierModel() {
      return sanitizeModel({
        title: 'Shippings',
        endpoint: this.$appConfig.api.baseUrl + 'shippings',
        fields: [
          {
            key: 'id',
            label: this.$gettext('ID'),
            component: (value, key, item) => {
              return {
                is: ShippingId,
                shipping: item
              }
            }
          },
          {
            key: 'status',
            label: this.$gettext('Status'),
            sortable: true,
            component: (value, key, item) => {
              return {
                is: ShippingsListItemStatus,
                shipping: item
              }
            },
            class: 'text-center'
          },
          {
            key: 'from',
            label: this.$gettext('From'),
            formatter: (value, key, item) => {
              return item.address_from.formatted_address
            }
          },
          {
            key: 'to',
            label: this.$gettext('To'),
            formatter: (value, key, item) => {
              return item.address_to.formatted_address
            }
          },
          {
            key: 'created_at',
            label: this.$gettext('Created At'),
            sortable: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'updated_at',
            label: this.$gettext('Updated At'),
            sortable: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          }
        ],
        filters: [
          'id'
        ]
      })
    },
    shippingsInOrder() {
      return sanitizeModel({
        title: 'Shippings',
        endpoint: this.$appConfig.api.baseUrl + 'shippings',
        editInPlace: true,
        listExtrasComponent: () => import('@/components/Shippings/ListInOrderExtras'),
        allowSelect: true,
        fields: [
          {
            key: 'id',
            label: this.$gettext('ID'),
            component: (value, key, item) => {
              return {
                is: ShippingId,
                shipping: item
              }
            }
          },
          {
            key: 'external_code',
            label: this.$gettext('External ID'),
            component: (value, key, item) => {
              return {
                is: ShippingExternalId,
                shipping: item
              }
            }
          },
          {
            key: 'status',
            label: this.$gettext('Status'),
            component: (value, key, item) => {
              return {
                is: ShippingsListItemStatus,
                shipping: item,
                withEta: true
              }
            },
            class: 'text-center'
          },
          {
            key: 'courier_commission',
            label: this.$gettext('Commission'),
            type: 'number',
            hidden: true,
            class: 'text-center',
            formatter: (value, key, item) => {
              return this.$options.filters.currency(value, window.country)
            }
          },
          {
            key: 'estimated_pickup_date',
            label: this.$gettext('Est. Pickup Date'),
            type: 'datetime',
            editable: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'pickup_note',
            label: this.$gettext('Pickup Note'),
            type: 'textarea',
            editable: true,
            default: '',
            props: {
              rows: 3,
              maxRows: 5
            },
            formatter: (value, key, item) => {
              return this.$options.filters.brize(value)
            }
          },
          {
            key: 'estimated_delivery_date',
            label: this.$gettext('Est. Delivery Date'),
            type: 'datetime',
            editable: true,
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'delivery_note',
            label: this.$gettext('Delivery Note'),
            type: 'textarea',
            editable: true,
            default: '',
            props: {
              rows: 3,
              maxRows: 5
            },
            formatter: (value, key, item) => {
              return this.$options.filters.brize(value)
            }
          },
          {
            key: 'items',
            label: this.ecosystemModules.items.label || this.$gettext('Items'),
            formatter: (value, key, item) => {
              if (value.length > 0) {
                let html = ''
                let descriptions = []
                for (const item of value) {
                  if (item.reference_code) {
                    html += `<span class="badge mr-1 badge-dark h6">${item.reference_code}</span>`
                  } else if (item.description) {
                    descriptions.push(item.description)
                  }
                }
                if (descriptions.length > 0) {
                  descriptions = descriptions.join(', ')
                } else {
                  descriptions = ''
                }
                return html + descriptions
              }
            },
            componentEdit: (value, key, item) => {
              this.orderServicesValue = item[key]
              return {
                is: EditFieldItems,
                props: {
                  placeholder: this.$gettext('Reference, or External ID'),
                  shipping: item,
                  singleItem: true,
                  value: item?.items || []
                }
              }
            }
          },
          {
            key: 'photos',
            label: this.$gettext('Documents'),
            formatter: (value, key, item) => {
              let html = ''
              if (value) {
                const success = value.filter(x => x.content_status === 'automatic' || x.content_status === 'reviewed')
                const warning = value.filter(x => x.content_status === 'manual')
                if (!value.length) {
                  html += `<span class="badge mr-1 badge-danger" title="${this.$gettext('No Documents')}">0</span>`
                }
                if (success.length) {
                  html += `<span class="badge mr-1 badge-success" title="${this.$gettext('Automatic and Reviewed')}">${success.length}</span>`
                }
                if (warning.length) {
                  html += `<span class="badge mr-1 badge-warning" title="${this.$gettext('Need Review')}">${warning.length}</span>`
                }
              }

              return html
            }
          },
          {
            key: 'courier_company_id',
            label: this.$gettext('Carrier'),
            editable: true,
            sortable: true,
            componentEdit: (value, key, item) => {
              return {
                is: CompanyAutocomplete,
                props: {
                  required: (item.status !== 'draft'),
                  restrictTo: [['relationship', 'eq', 'provider']],
                  preQuery: item.courier_company ? (item.courier_company.external_id || item.courier_company.name) : null
                },
                on: {
                  selected: (result) => {
                    if (result) {
                      item[key] = result.id
                    } else {
                      item[key] = null
                    }
                  }
                }
              }
            },
            formatter: (value, key, item) => {
              if (item.courier_company) {
                let html = `<strong>${item.courier_company.external_id || item.courier_company.name || item.courier_company.id}</strong>`
                html += item.courier_company ? `<br><span class="badge" style="background-color: ${this.stringToColour(' ' + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id)};${this.textContrastColor(this.stringToColour(' ' + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id + item.courier_company.id))}">${item.courier_company.name}</span>` : ''
                return html
              }
            }
          },
          {
            key: 'courier',
            sortable: false,
            label: this.$gettext('Courier'),
            component: (value, key, item) => {
              return {
                is: 'user-card',
                user: item.courier || {},
                profilePicture: false,
                withTitles: false
              }
            },
            componentEdit: (value, key, item) => {
              return {
                is: EditFieldCourier,
                props: {
                  placeholder: this.$gettext('Reference, or External ID'),
                  shipping: item,
                  value: item?.courier || {}
                },
                on: {
                  success: (courier) => {
                    item.courier_id = courier.id
                    item.courier = courier
                  }
                }
              }
            }
          }
        ],
        includes: [
          'items',
          'items.item_tracking',
          'courier_company',
          'photos',
          'courier.courier_available.tracking'
        ]
      })
    }
  }
}
